import { useMemo, useState } from 'react';
import { formatMoney, parseMoney, type Centavos, type Settings } from '@abastece/shared';
import { ACCOUNTS, type LineaReporte } from '@abastece/core';
import { cambioBp, type DineroDeAnalisis } from '../lib/analisis.js';
import { alcanza, avanceDelPresupuesto, costoDeContratar, horasExtremas, perdidasDelPeriodo, planillaDelPeriodo, type HoraDeCaja } from '../lib/egresos.js';
import { emitir } from '../lib/emitir.js';
import { usePuede } from '../lib/sesion.js';
import { localeActual, useT } from '../i18n/index.js';
import { etiquetaCuenta } from '../i18n/dominio.js';
import { Campo, Interruptor } from './Campo.js';
import { Barras, Cambio } from './Graficas.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { Nada, Seccion, parte, pct, useFechas } from './PiezasDeAnalisis.js';

/**
 * What «Gastos» in «Análisis» says besides where the money goes and the break-even (the owner,
 * 2026-10-13: «let's build all of it»): the month's budget, what is lost, what the payroll takes,
 * the till's hours against the sales, and whether a new cost fits. The arithmetic is
 * `lib/egresos.ts`, read from the books as «En qué se va» reads them.
 */

const nombreDeCuenta = (codigo: string, config: Settings): string => {
  const a = Object.values(ACCOUNTS).find((x) => x.code === codigo);
  return etiquetaCuenta(codigo, a?.name ?? codigo, config.cuentasBanco);
};

/**
 * «Presupuesto del mes», on a month: each expense account with a budget against what it has spent,
 * and what went to the rest. «Presupuestos» sets them, a setting like the month's goals.
 */
export function PresupuestoDelMes({
  gastos,
  recientes,
  config,
  dinero,
}: {
  /** The month's expense accounts. */
  gastos: readonly LineaReporte[];
  /** Those of the last ninety days: the accounts a budget is offered for. */
  recientes: readonly LineaReporte[];
  config: Settings;
  dinero: DineroDeAnalisis;
}) {
  const t = useT();
  const puede = usePuede('configurar');
  const [abierto, setAbierto] = useState(false);
  const a = avanceDelPresupuesto(config.presupuestosGastos, gastos, (codigo) => nombreDeCuenta(codigo, config));
  return (
    <Seccion
      titulo={t('analisis.presupuesto.titulo')}
      ayuda={t('analisis.presupuesto.ayuda')}
      datos="presupuesto"
      accion={
        puede ? (
          <button type="button" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" onClick={() => setAbierto(true)} data-poner-presupuestos>
            <Ico icono="editar" className="size-4" />
            {t('analisis.presupuesto.boton')}
          </button>
        ) : undefined
      }
    >
      {a.filas.length === 0 ? (
        <Nada texto={t('analisis.presupuesto.nada')} />
      ) : (
        <>
          <p className="mb-3 text-sm text-slate-300" data-presupuesto-total>
            {t('analisis.presupuesto.total', { gastado: dinero(a.gastado), presupuesto: dinero(a.presupuesto) })}
          </p>
          <ul className="flex flex-col gap-3">
            {a.filas.map((f) => {
              const pasado = f.gastado > f.presupuesto;
              return (
                <li key={f.codigo} data-presupuesto={f.codigo}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 wrap-break-word">{f.nombre}</span>
                    <span className="tabular shrink-0">{t('analisis.metas.deMeta', { llevado: dinero(f.gastado), meta: dinero(f.presupuesto) })}</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-800">
                    <div className={`h-full rounded-full ${pasado ? 'bg-rose-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, f.parteBp / 100)}%` }} />
                  </div>
                  <p className={`mt-1 text-xs ${pasado ? 'text-rose-400' : 'text-slate-500'}`} data-presupuesto-dice>
                    {pasado
                      ? t('analisis.presupuesto.pasado', { monto: dinero(f.gastado - f.presupuesto) })
                      : t('analisis.presupuesto.quedan', { monto: dinero(f.presupuesto - f.gastado), pct: pct(f.parteBp) })}
                  </p>
                </li>
              );
            })}
          </ul>
          {a.sinPresupuesto.cuentas > 0 && (
            <p className="mt-3 text-xs text-slate-500" data-sin-presupuesto>
              {t('analisis.presupuesto.sinPresupuesto', { monto: dinero(a.sinPresupuesto.monto), n: a.sinPresupuesto.cuentas })}
            </p>
          )}
        </>
      )}
      {abierto && <Presupuestos recientes={recientes} config={config} onCerrar={() => setAbierto(false)} />}
    </Seccion>
  );
}

/** What a field typed holds: null while empty, undefined when it is not an amount. */
function leerMonto(texto: string): number | null | undefined {
  if (texto.trim() === '') return null;
  const c = parseMoney(texto);
  return c !== null && c > 0 ? c : undefined;
}

/** «Presupuestos»: what each expense account may spend a month, in córdobas; empty is none. */
function Presupuestos({ recientes, config, onCerrar }: { recientes: readonly LineaReporte[]; config: Settings; onCerrar: () => void }) {
  const t = useT();
  // The accounts the shop has spent on lately, and any with a budget already.
  const cuentas = useMemo(
    () => [...new Set([...recientes.filter((l) => l.monto > 0).map((l) => l.codigo), ...Object.keys(config.presupuestosGastos)])].sort(),
    [recientes, config.presupuestosGastos],
  );
  const [textos, setTextos] = useState<Record<string, string>>(() =>
    Object.fromEntries(cuentas.map((codigo) => [codigo, config.presupuestosGastos[codigo] ? (config.presupuestosGastos[codigo]! / 100).toFixed(2) : ''])),
  );
  const [trabajando, setTrabajando] = useState(false);
  const leidos = Object.fromEntries(cuentas.map((codigo) => [codigo, leerMonto(textos[codigo] ?? '')]));
  const malos = Object.values(leidos).some((v) => v === undefined);
  async function guardar() {
    if (malos) return;
    const valor = Object.fromEntries(Object.entries(leidos).filter((x): x is [string, number] => typeof x[1] === 'number'));
    setTrabajando(true);
    try {
      if (JSON.stringify(valor) !== JSON.stringify(Object.fromEntries(Object.entries(config.presupuestosGastos).sort())))
        await emitir('config.cambiada', { clave: 'presupuestosGastos', valor, anterior: config.presupuestosGastos });
      onCerrar();
    } finally {
      setTrabajando(false);
    }
  }
  return (
    <Modal
      titulo={t('analisis.presupuesto.boton')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={trabajando || malos || cuentas.length === 0} onClick={() => void guardar()} data-presupuestos-guardar>
          <Ico icono="guardar" />
          {t('comun.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-presupuestos>
        <p className="text-sm text-slate-400">{t('analisis.presupuesto.explica')}</p>
        {cuentas.length === 0 ? (
          <Nada texto={t('analisis.presupuesto.sinCuentas')} />
        ) : (
          cuentas.map((codigo) => (
            <Campo
              key={codigo}
              etiqueta={nombreDeCuenta(codigo, config)}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={textos[codigo] ?? ''}
              onChange={(e) => setTextos((x) => ({ ...x, [codigo]: e.target.value }))}
              error={leidos[codigo] === undefined ? t('analisis.metas.mal') : null}
              data-presupuesto-cuenta={codigo}
            />
          ))
        )}
      </div>
    </Modal>
  );
}

/**
 * «Lo que se pierde»: the period's losses by the account each went to, against the sales and the
 * period before, and the last six months' together.
 */
export function LoQueSePierde({
  gastos,
  antes,
  ingresos,
  meses,
  config,
  dinero,
}: {
  gastos: readonly LineaReporte[];
  antes: readonly LineaReporte[];
  ingresos: number;
  /** The last months', oldest first: `YYYY-MM` and what they lost. */
  meses: readonly { mes: string; total: number }[];
  config: Settings;
  dinero: DineroDeAnalisis;
}) {
  const t = useT();
  const ahora = perdidasDelPeriodo(gastos);
  const previo = perdidasDelPeriodo(antes);
  const mes = new Intl.DateTimeFormat(localeActual(), { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return (
    <Seccion titulo={t('analisis.perdidas.titulo')} ayuda={t('analisis.perdidas.ayuda')} datos="perdidas">
      {ahora.total <= 0 ? (
        <Nada texto={t('analisis.perdidas.nada')} />
      ) : (
        <>
          <p className="mb-3 flex flex-wrap items-baseline gap-x-2 text-sm text-slate-300" data-perdidas-total>
            <span>{t('analisis.perdidas.total', { monto: dinero(ahora.total), pct: pct(parte(ahora.total, ingresos)) })}</span>
            <Cambio bp={cambioBp(ahora.total, previo.total)} alReves />
          </p>
          <Barras
            filas={ahora.lineas.map((l) => ({ clave: l.codigo, etiqueta: nombreDeCuenta(l.codigo, config), valor: l.monto, texto: dinero(l.monto) }))}
            tono="bg-rose-500/70"
            datos="perdidas"
          />
        </>
      )}
      {meses.some((m) => m.total > 0) && (
        <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm min-[380px]:grid-cols-3" data-perdidas-meses>
          {meses.map((m) => (
            <li key={m.mes} className="flex items-baseline justify-between gap-2 rounded-lg bg-slate-800/60 px-2 py-1.5" data-mes={m.mes}>
              <span className="whitespace-nowrap text-slate-400">{mes.format(new Date(`${m.mes}-01T00:00:00Z`))}</span>
              <span className="tabular text-xs">{dinero(m.total)}</span>
            </li>
          ))}
        </ul>
      )}
    </Seccion>
  );
}

/** «La planilla»: what the payroll cost in the period, against the sales and the gross profit. */
export function LaPlanilla({
  ahora,
  antes,
  dinero,
}: {
  ahora: Parameters<typeof planillaDelPeriodo>[0];
  antes: Parameters<typeof planillaDelPeriodo>[0];
  dinero: DineroDeAnalisis;
}) {
  const t = useT();
  const p = planillaDelPeriodo(ahora);
  const previo = planillaDelPeriodo(antes);
  return (
    <Seccion titulo={t('analisis.planilla.titulo')} ayuda={t('analisis.planilla.ayuda')} datos="planilla">
      {p.monto <= 0 ? (
        <Nada texto={t('analisis.planilla.nada')} />
      ) : (
        <div className="flex flex-col gap-1 text-sm text-slate-300">
          <p className="flex flex-wrap items-baseline gap-x-2" data-planilla-costo>
            <span>{t('analisis.planilla.costo', { monto: dinero(p.monto) })}</span>
            <Cambio bp={cambioBp(p.monto, previo.monto)} alReves />
          </p>
          {p.deVentasBp !== null && <p data-planilla-ventas>{t('analisis.planilla.deVentas', { pct: pct(p.deVentasBp) })}</p>}
          {p.deUtilidadBp !== null && (
            <p className={p.deUtilidadBp > 10_000 ? 'text-rose-400' : ''} data-planilla-utilidad>
              {t('analisis.planilla.deUtilidad', { pct: pct(p.deUtilidadBp) })}
            </p>
          )}
        </div>
      )}
    </Seccion>
  );
}

/** «Horas de caja»: the hours the till was open against the sales they rang. */
export function HorasDeCaja({ horas }: { horas: { abiertas: number; sinVenta: number; celdas: readonly HoraDeCaja[] } | null }) {
  const t = useT();
  const f = useFechas();
  const formato = new Intl.NumberFormat(localeActual(), { maximumFractionDigits: 1 });
  const lista = (hs: readonly HoraDeCaja[], datos: string) => (
    <ul className="mt-1 flex flex-col gap-1" data-horas={datos}>
      {hs.map((h) => (
        <li key={`${h.dia}-${h.hora}`} className="flex items-baseline justify-between gap-3 rounded-lg bg-slate-800/60 px-3 py-1.5" data-hora={`${h.dia}-${h.hora}`}>
          <span>{t('analisis.horas.cuando', { dia: f.diasLargos[h.dia]!, desde: `${h.hora}:00`, hasta: `${h.hora + 1}:00` })}</span>
          <span className="tabular shrink-0 text-xs text-slate-400">{t('analisis.horas.porHora', { n: formato.format(h.porHora) })}</span>
        </li>
      ))}
    </ul>
  );
  const extremas = horas ? horasExtremas(horas.celdas) : null;
  return (
    <Seccion titulo={t('analisis.horas.titulo')} ayuda={t('analisis.horas.ayuda')} datos="horas-caja">
      {!horas || !extremas ? (
        <Nada texto={t('analisis.horas.nada')} />
      ) : (
        <div className="flex flex-col gap-3 text-sm text-slate-300">
          <p data-horas-total>{t('analisis.horas.total', { n: horas.abiertas, m: horas.sinVenta })}</p>
          {extremas.movidas.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400">{t('analisis.horas.movidas')}</h3>
              {lista(extremas.movidas, 'movidas')}
            </div>
          )}
          {extremas.flojas.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400">{t('analisis.horas.flojas')}</h3>
              {lista(extremas.flojas, 'flojas')}
            </div>
          )}
        </div>
      )}
    </Seccion>
  );
}

/**
 * «¿Me alcanza?»: a new person or a new fixed expense, against what the shop has been leaving: what
 * it costs a month, what would be left, and what more would have to sell to pay for it.
 */
export function MeAlcanza({ ultimos, dias, config }: { ultimos: { ingreso: number; utilidadBruta: number; utilidadNeta: number }; dias: number; config: Settings }) {
  const t = useT();
  const [abierto, setAbierto] = useState(false);
  return (
    <Seccion
      titulo={t('analisis.alcanza.titulo')}
      ayuda={t('analisis.alcanza.ayuda', { n: dias })}
      datos="alcanza"
      accion={
        <button type="button" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" onClick={() => setAbierto(true)} data-alcanza-abrir>
          <Ico icono="calcular" className="size-4" />
          {t('analisis.oferta.boton')}
        </button>
      }
    >
      <p className="text-sm text-slate-400">{t('analisis.alcanza.explica')}</p>
      {abierto && <HojaMeAlcanza ultimos={ultimos} dias={dias} config={config} onCerrar={() => setAbierto(false)} />}
    </Seccion>
  );
}

function HojaMeAlcanza({
  ultimos,
  dias,
  config,
  onCerrar,
}: {
  ultimos: { ingreso: number; utilidadBruta: number; utilidadNeta: number };
  dias: number;
  config: Settings;
  onCerrar: () => void;
}) {
  const t = useT();
  const [que, setQue] = useState<'persona' | 'gasto'>('persona');
  const [texto, setTexto] = useState('');
  const [conInss, setConInss] = useState(true);
  const [conPrestaciones, setConPrestaciones] = useState(true);
  const monto = leerMonto(texto);
  const persona = que === 'persona' && typeof monto === 'number' ? costoDeContratar(monto as Centavos, config, { conInss, conPrestaciones }) : null;
  const mensual = persona ? persona.total : typeof monto === 'number' ? monto : null;
  const a = mensual !== null ? alcanza({ mensual, ...ultimos, dias }) : null;
  return (
    <Modal titulo={t('analisis.alcanza.titulo')} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4" data-alcanza>
        <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="radiogroup" aria-label={t('analisis.alcanza.titulo')}>
          {(['persona', 'gasto'] as const).map((x) => (
            <button
              key={x}
              type="button"
              role="radio"
              aria-checked={que === x}
              data-sin-icono="pestana"
              className={`min-h-10 flex-1 rounded-lg px-2 text-sm transition ${que === x ? 'bg-slate-700 font-semibold text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}
              onClick={() => setQue(x)}
              data-alcanza-que={x}
            >
              {t(x === 'persona' ? 'analisis.alcanza.persona' : 'analisis.alcanza.gasto')}
            </button>
          ))}
        </div>
        <Campo
          etiqueta={t(que === 'persona' ? 'analisis.alcanza.sueldo' : 'analisis.alcanza.monto')}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.00"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          error={monto === undefined ? t('analisis.simular.mal') : null}
          data-alcanza-monto
        />
        {que === 'persona' && (
          <div className="-my-2">
            <Interruptor etiqueta={t('planilla.pagaInss')} descripcion={t('planilla.pagaInssAyuda')} checked={conInss} onChange={setConInss} />
            <Interruptor etiqueta={t('planilla.prestaciones')} descripcion={t('planilla.prestacionesAyuda')} checked={conPrestaciones} onChange={setConPrestaciones} />
          </div>
        )}
        {a && mensual !== null && (
          <div className="flex flex-col gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300" data-alcanza-resultado>
            {persona && (
              <p data-alcanza-costo>
                {t('analisis.alcanza.costo', {
                  total: formatMoney(persona.total),
                  sueldo: formatMoney(persona.sueldo),
                  patronal: formatMoney(persona.patronal),
                  prestaciones: formatMoney(persona.prestaciones),
                })}
              </p>
            )}
            <p data-alcanza-queda>{t('analisis.alcanza.queda', { n: dias, queda: formatMoney(a.quedaAlMes) })}</p>
            <p className={`font-medium ${a.quedaria < 0 ? 'text-rose-400' : 'text-emerald-300'}`} data-alcanza-quedaria>
              {t(a.quedaria < 0 ? 'analisis.alcanza.noAlcanza' : 'analisis.alcanza.quedaria', { monto: formatMoney(a.quedaria < 0 ? (-a.quedaria as Centavos) : a.quedaria) })}
            </p>
            {a.venderAlMes !== null && a.venderAlDia !== null && a.margenBp !== null && (
              <p data-alcanza-vender>
                {t('analisis.alcanza.vender', { mes: formatMoney(a.venderAlMes), dia: formatMoney(a.venderAlDia), pct: pct(a.margenBp) })}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
